import { apiClient } from '@/features/shared/api/client';

export type StudentTelemetryEventName =
  | 'session_started' | 'session_ended' | 'route_viewed' | 'question_viewed' | 'question_started'
  | 'question_submitted' | 'question_abandoned' | 'question_retried' | 'audio_play' | 'audio_seek'
  | 'audio_error' | 'recording_started' | 'recording_stopped' | 'recording_upload_failed' | 'answer_opened'
  | 'answer_closed' | 'navigation_opened' | 'navigation_expanded' | 'navigation_located' | 'contact_qr_exposed'
  | 'contact_qr_clicked' | 'contact_copied' | 'contact_email_clicked' | 'contact_external_clicked'
  | 'entry_check_completed' | 'entry_check_skipped' | 'ui_error';

type PendingEvent = {
  eventName: StudentTelemetryEventName;
  eventVersion: number;
  occurredAt: string;
  durationMs?: number;
  sessionId: string;
  route?: string;
  questionId?: string;
  questionTypeCode?: string;
  attemptId?: string;
  properties: Record<string, string | number | boolean | null>;
};

let queue: PendingEvent[] = [];
let flushTimer: number | null = null;
let initialized = false;

function sessionId() {
  const key = 'pps:telemetry-session';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const value = crypto.randomUUID();
    sessionStorage.setItem(key, value);
    return value;
  } catch {
    return 'session-unavailable';
  }
}

export async function flushStudentTelemetry() {
  if (!queue.length) return;
  const batch = queue.splice(0, 100);
  try {
    await apiClient.post('/me/analytics/events', { events: batch.map((item) => ({ ...item, eventName: item.eventName, sessionId: item.sessionId })) }, { keepalive: true });
  } catch {
    queue = [...batch, ...queue].slice(-100);
  }
}

export function trackStudentEvent(
  eventName: StudentTelemetryEventName,
  context: Omit<Partial<PendingEvent>, 'eventName' | 'eventVersion' | 'occurredAt' | 'sessionId'> = {},
) {
  queue.push({
    eventName,
    eventVersion: 1,
    occurredAt: new Date().toISOString(),
    sessionId: sessionId(),
    properties: {},
    ...context,
  });
  if (queue.length >= 20) {
    void flushStudentTelemetry();
  } else if (flushTimer === null && typeof window !== 'undefined') {
    flushTimer = window.setTimeout(() => {
      flushTimer = null;
      void flushStudentTelemetry();
    }, 5_000);
  }
}

export function initializeStudentTelemetry() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  const startedAt = performance.now();
  let ended = false;
  trackStudentEvent('session_started', { route: window.location.pathname });
  const endSession = () => {
    if (ended) return;
    ended = true;
    trackStudentEvent('session_ended', {
      route: window.location.pathname,
      durationMs: Math.max(0, Math.round(performance.now() - startedAt)),
    });
    void flushStudentTelemetry();
  };
  window.addEventListener('pagehide', endSession);
  window.addEventListener('beforeunload', endSession);
}
